import {
    DatacentralHookForm,
    type DatacentralHookFormDataItem,
} from '@/components/ui/form-field/datacentral-hook-form'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import {
    createProductPrice,
    updateProductPrice,
} from '@/modules/Settings/services/product-price'
import {
    CreateProductPricePayload,
    ProductPrice,
} from '@/modules/Settings/types/product-price'
import { ModalProps } from '@/types/shared'
import dayjs from 'dayjs'
import { Loader2, Save } from 'lucide-react'
import React, { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dialog } from 'vnetwork-ui'

export interface CreateEditProductPriceModalProps extends ModalProps {
    productId: number
    productPrice?: ProductPrice | null
}

const CURRENCY_OPTIONS = [
    { label: 'VND', value: 'vi' },
    { label: 'USD', value: 'usd' },
    { label: 'EUR', value: 'eur' },
]

const CreateEditProductPriceModal: React.FC<
    CreateEditProductPriceModalProps
> = ({ productId, productPrice, onClose, open }) => {
    const { t } = useTranslation('settings')
    const isEdit = !!productPrice

    const formControl = useForm<CreateProductPricePayload>({
        defaultValues: productPrice
            ? {
                  currency: productPrice.currency,
                  price: productPrice.price,
                  start_date: dayjs(productPrice.start_date).format(
                      'YYYY-MM-DD'
                  ),
                  end_date: productPrice.end_date
                      ? dayjs(productPrice.end_date).format('YYYY-MM-DD')
                      : undefined,
              }
            : {
                  currency: 'vi',
                  price: 0,
                  start_date: dayjs().format('YYYY-MM-DD'),
                  end_date: undefined,
              },
    })

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: async (data: CreateProductPricePayload) => {
                if (isEdit && productPrice) {
                    return updateProductPrice(
                        productId,
                        productPrice.product_price_id,
                        data
                    )
                }
                return createProductPrice(productId, data)
            },
        },
        {
            successMessage: isEdit
                ? t('price-updated-successfully')
                : t('price-created-successfully'),
            reactQueryKeys: [
                QUERY_KEYS.PRODUCT_PRICES,
                QUERY_KEYS.PRODUCTS_SEARCH,
            ],
            onSuccess: () => onClose(),
        }
    )

    const formData: DatacentralHookFormDataItem<CreateProductPricePayload>[] =
        useMemo(
            () => [
                {
                    formItemProps: {
                        name: 'currency',
                        type: 'select',
                        label: t('currency'),
                        options: CURRENCY_OPTIONS,
                        required: true,
                    },
                    colSpan: 6,
                },
                {
                    formItemProps: {
                        name: 'price',
                        type: 'number',
                        label: t('price'),
                        placeholder: '500000',
                        required: true,
                        min: 0,
                    },
                    colSpan: 6,
                },
                {
                    formItemProps: {
                        name: 'start_date',
                        type: 'datepicker',
                        label: t('start-date'),
                        required: true,
                    },
                    colSpan: 6,
                },
                {
                    formItemProps: {
                        name: 'end_date',
                        type: 'datepicker',
                        label: t('end-date'),
                        placeholder: t('end-date-placeholder'),
                    },
                    colSpan: 6,
                },
            ],
            [t]
        )

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={isEdit ? t('edit-price') : t('create-price')}
            maxWidth="sm"
        >
            <DatacentralHookForm<CreateProductPricePayload>
                formControl={formControl}
                data={formData}
                onSubmit={(data) =>
                    mutateAsync({
                        ...data,
                        end_date: data.end_date || undefined,
                    })
                }
                columns={12}
                actions={[
                    {
                        title: t('cancel'),
                        type: 'button',
                        variant: 'outline',
                        handler: onClose,
                        disabled: isPending,
                    },
                    {
                        title: isEdit ? t('update') : t('create'),
                        type: 'submit',
                        variant: 'default',
                        disabled: isPending,
                        loading: isPending,
                        startIcon: isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Save className="h-4 w-4" />
                        ),
                    },
                ]}
            />
        </Dialog>
    )
}

export default CreateEditProductPriceModal
